import type {TravelType} from '@/modules/common/context/QueryParams/models';
import type {ExtractPropsWithoutRef} from '@/types';

import {useBreakpointUp} from '@travel-frontend/core';
import {IconFilter} from '@travel-frontend/icons';
import {forwardRef} from 'react';
import {useTranslation} from 'react-i18next';

import {ControlBar as ControlBarUI} from '@/components/ControlBar';
import {CountBadge} from '@/components/CountBadge';
import {FilterOverlayTrigger} from '@/components/FilterOverlayTrigger';
import {SearchSummary} from '@/modules/common/components/ControlBar/components/SearchSummary';
import {useSearchFormDefaultValues} from '@/modules/common/components/SearchBar/hooks/useSearchFormDefaultValues';
import {useFilterOverlay} from '@/modules/common/context/FilterOverlay';
import {useSearchFormOverlay} from '@/modules/common/context/SearchFormOverlay';
import {testId} from '@/utils/testId';

type Props = ExtractPropsWithoutRef<typeof ControlBarUI> & {
  activeFilterCount: number;
  travelType: TravelType;
  tabRowEnabled?: boolean;
};

export const ControlBar = forwardRef<HTMLDivElement, Props>(function ControlBar(
  {activeFilterCount, travelType, tabRowEnabled, ...restProps},
  ref
) {
  const {t} = useTranslation('general');
  const mdUp = useBreakpointUp('md');

  const defaultValues = useSearchFormDefaultValues({
    travelType,
  });

  const {toggleFilterOverlay} = useFilterOverlay();
  const {openSearchFormOverlay} = useSearchFormOverlay();

  return (
    <ControlBarUI
      ref={ref}
      data-testid={testId('search-sticky-bar')}
      {...restProps}
    >
      <SearchSummary
        onClick={openSearchFormOverlay}
        ctaElement={
          tabRowEnabled ? undefined : (
            <FilterOverlayTrigger
              aria-label={t('buttons.filter')}
              category="primary"
              variant={
                import.meta.env.VITE_PORTAL === 'reisen-de' ||
                import.meta.env.VITE_PORTAL === 'reisegeier-de' ||
                import.meta.env.VITE_PORTAL === 'reisegeier-at' ||
                import.meta.env.VITE_PORTAL === 'reisegeier-ch'
                  ? 'inverted'
                  : 'interaction'
              }
              onClick={toggleFilterOverlay}
              data-testid={testId('filter-open')}
            >
              <IconFilter />
              {mdUp && t('buttons.filter')}
              <CountBadge
                backgroundColor="interactionSecondaryHover"
                count={activeFilterCount}
              />
            </FilterOverlayTrigger>
          )
        }
        defaultValues={defaultValues}
        multiroom={true}
      />
    </ControlBarUI>
  );
});
